import { select, Store } from '@ngrx/store';
import { NativeClientService } from '@shared/services/native-client.service';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree, } from '@angular/router';
import { Observable, of } from 'rxjs';
import { sessionAuthFeature } from '../store/reducers/session-auth.reducer';
import { OnboardingService } from '../services/onboarding.service';

const START_RECOVERY_PATH = 'two-factor/recovery?inFlow=true';

/**
 * Checks if user is authenticated and handles recovery mode
 * For native apps: checks token presence
 * For web apps: checks OAuth authentication
 */
export const authRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const nativeClientService = inject(NativeClientService);
  const store = inject(Store);
  const router = inject(Router);
  const onboardingService = inject(OnboardingService);

  if (nativeClientService.isNative) {
    return store.pipe(
      select(sessionAuthFeature.selectToken),
      switchMap((token) =>
        token == null ? noToken() : checkRecoveryMode(state.url),
      ),
    );
  }

  if (
    state.url.includes('/two-factor/recovery') &&
    !state.url.includes('inFlow')
  ) {
    return authService
      .hiddenLogout()
      .pipe(map(() => errorPathHandler(START_RECOVERY_PATH)));
  }

  // On page refresh loginRequest is null — reconstruct it from the stored
  // CSRF token so the guard can pass without triggering a new BFF OAuth flow
  // (which would prompt for 2FA on credential-only sessions).
  authService.initFromSessionStorage();

  const loginRequest = authService.getOAuth();
  if (loginRequest) {
    return authService
      .isAuthenticated$()
      .pipe(
        switchMap((isAuthenticated) =>
          isAuthenticated
            ? checkRecoveryMode(state.url)
            : of(errorPathHandler(state.url)),
        ),
      );
  } else {
    return of(errorPathHandler(state.url));
  }

  /**
   * Error Handler if the user cannot access a route
   *
   * @param route route to be checked
   */
  function errorPathHandler(route: string): boolean {
    authService.connectOAuth(false, route);
    return false;
  }

  function noToken(): Observable<boolean | UrlTree> {
    nativeClientService.nativeLogin();
    return of(true);
  }

  function checkRecoveryMode(route: string): Observable<boolean | UrlTree> {
    if (route.includes(START_RECOVERY_PATH)) return of(true);

    return onboardingService.isRecoveryMode().pipe(
      map((isRecovery) => {
        if (!isRecovery) {
          return true;
        } else {
          return nativeClientService.isNative
            ? router.parseUrl(`no-app-access`)
            : errorPathHandler(START_RECOVERY_PATH);
        }
      }),
    );
  }
};
